/**
 * Costs & Margin Hub API — wraps Phase A REST behind FF_COSTS_MARGIN_TAB.
 * User-facing: Subcontract Amount (DB may still use committed_cents).
 */

import { apiFetch } from './client'

export type MoneyField = {
  amount_cents: number
  amount: number
  formatted: string
}

export type CommitmentRow = {
  project_vendor_id: number
  vendor_id: number
  company_name: string | null
  trade: string | null
  committed_cents: number
  invoiced_cents: number
  paid_cents: number
  not_yet_billed_cents: number
  last_invoice_date: string | null
}

export type JobCostRow = {
  id: number
  project_id: number
  description: string
  category: string
  amount_cents: number
  incurred_on: string | null
  vendor_name: string | null
  due_date: string | null
  paid_at: string | null
  receipt_upload_id: number | null
}

export type ChangeOrderTwoWay = {
  id: number
  co_number: number
  description: string
  amount: number
  status: string
  direction: 'owner' | 'sub' | string
  vendor_id: number | null
  linked_change_order_id: number | null
  linked_sub_amounts?: number[]
  linked_pair_spread?: number | null
  app_number?: number
  vendor_name?: string | null
}

export type AnsweredState = {
  contract: { answered: boolean }
  subcontract_amounts: Array<{
    project_vendor_id: number
    vendor_id?: number
    company_name?: string | null
    trade?: string | null
    answered: boolean
    subcontract_amount_cents: number
    invoiced_cents: number
    paid_cents: number
  }>
  sub_change_orders: { answered: boolean; count: number }
  labor: { answered: boolean; cost_count?: number; last_labor_on?: string | null }
  overhead: { answered: boolean; cost_count?: number }
}

export type CostsMarginPayload = {
  project_id: number
  project_name: string
  original_contract: MoneyField
  revised_contract: MoneyField
  projected_cost: MoneyField
  projected_margin: MoneyField
  projected_margin_pct: number | null
  completeness: {
    cost_count: number
    commitment_count: number
    last_labor_on: string | null
  }
  completeness_line: string
  peak_out_of_pocket: {
    peak_cents: number
    peak_date: string | null
    days_underwater: number
    peak: MoneyField
  }
  commitments: CommitmentRow[]
  unmatched_sub_cos: ChangeOrderTwoWay[]
  change_orders: {
    owner: ChangeOrderTwoWay[]
    sub: ChangeOrderTwoWay[]
    all: ChangeOrderTwoWay[]
  }
  answered_state: AnsweredState
}

type ApiEnvelope<T> = { data: T; error: string | null; message?: string | null }

async function wrap<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await apiFetch<ApiEnvelope<T>>(method, path, body)
  return res.data
}

export function fetchCostsMargin(projectId: number) {
  return wrap<CostsMarginPayload>('GET', `/api/projects/${projectId}/costs-margin`)
}

export async function fetchJobCosts(projectId: number): Promise<JobCostRow[]> {
  const data = await wrap<{ costs: JobCostRow[]; count: number }>(
    'GET',
    `/api/projects/${projectId}/job-costs`,
  )
  return data.costs ?? []
}

export type CreateJobCostBody = {
  description: string
  category: string
  amount_cents: number
  incurred_on?: string | null
  vendor_name?: string | null
  due_date?: string | null
  paid_at?: string | null
}

export async function createJobCost(projectId: number, body: CreateJobCostBody) {
  const data = await wrap<{ job_cost: JobCostRow }>(
    'POST',
    `/api/projects/${projectId}/job-costs`,
    body,
  )
  return data.job_cost
}

export async function updateJobCost(
  projectId: number,
  costId: number,
  body: Partial<CreateJobCostBody>,
) {
  const data = await wrap<{ job_cost: JobCostRow }>(
    'PATCH',
    `/api/projects/${projectId}/job-costs/${costId}`,
    body,
  )
  return data.job_cost
}

export async function deleteJobCost(projectId: number, costId: number) {
  const data = await wrap<{ job_cost: JobCostRow }>(
    'DELETE',
    `/api/projects/${projectId}/job-costs/${costId}`,
  )
  return data.job_cost
}

export async function setSubcontractAmount(
  projectId: number,
  vendorId: number,
  amount_cents: number,
) {
  const data = await wrap<{ project_vendor: CommitmentRow; subcontract_amount_cents: number }>(
    'PUT',
    `/api/projects/${projectId}/vendors/${vendorId}/subcontract-amount`,
    { amount_cents },
  )
  return data.project_vendor
}

export type CreateSubCoBody = {
  description: string
  amount_dollars: number
  project_vendor_id?: number
  vendor_name?: string
  linked_change_order_id?: number | null
}

export async function createSubChangeOrder(projectId: number, body: CreateSubCoBody) {
  const data = await wrap<{ change_order: ChangeOrderTwoWay }>(
    'POST',
    `/api/projects/${projectId}/sub-change-orders`,
    body,
  )
  return data.change_order
}
